import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { UntypedFormControl } from '@angular/forms';

import { TranslocoService } from '@jsverse/transloco';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { groupNameValidatorFactory } from 'app/modules/configuration/configuration-utils';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { Group, GroupService } from 'app/providers/semantria-api/group.service';
import {
  SemantriaUser,
  SemantriaUsersService,
} from 'app/providers/semantria-api/semantria-users.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { StorageListResult } from 'app/providers/storage-api.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { UserAddDialogOptions } from '../user-add-dialog/user-add-dialog.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-group-users',
  templateUrl: './group-users.component.html',
  styleUrls: ['./group-users.component.scss'],
  standalone: false,
})
export class GroupUsersComponent implements OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() group: Group | undefined;
  @Input() groupList: Group[];
  @Input() isLoading: boolean;

  @Output() groupChange = new EventEmitter<Group>();
  @Output() groupDelete = new EventEmitter<Group>();

  groupName: UntypedFormControl;
  groupConfigCount: number;
  groupProjectCount: number;
  groupRouteCount: number;

  allUsers: SemantriaUser[];
  remainingUsers: SemantriaUser[];
  groupUsers: SemantriaUser[];

  userMemberships: Map<string, Map<string, string[]>>;

  showGroupUsersModal = false;
  groupUsersData: UserAddDialogOptions;

  canDeleteGroup: boolean;
  private session: Session;
  public allProjects: StorageListResult<Project> = {};
  isExecutive: boolean;

  constructor(
    private groupService: GroupService,
    private userService: SemantriaUsersService,
    private authService: AuthService,
    private dialogService: DialogService,
    private projectService: ProjectService,
    private alertService: AlertService,
    private systemService: SystemService,
    private changeDetector: ChangeDetectorRef,
    private translocoService: TranslocoService
  ) {
    this.remainingUsers = [];
    this.userMemberships = new Map<string, Map<string, string[]>>();
    this.groupName = new UntypedFormControl();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.group && changes.group.currentValue) {
      this.isLoading = true;
      this.group = changes.group.currentValue;
      this.groupName.setValidators(groupNameValidatorFactory(this.groupList, this.group));
      this.groupName.setValue(this.group.group_name, { emitEvent: false });
      this.groupConfigCount = this.group.permissions.filter(
        (perm) => perm.config_id && !perm.evaluation && !perm.removed
      ).length;

      this.session = await this.authService.getSession();
      this.isExecutive = this.authService.isExecutive;
      this.allProjects = await this.projectService.searchProjects(
        null,
        100,
        true,
        true,
        this.isExecutive,
        this.group.group_id
      );

      this.groupProjectCount = this.allProjects.count;
      this.groupRouteCount = this.group.routes ? this.group.routes.length : 0;

      await this.refreshUsers();
      this.isLoading = false;
      this.changeDetector.detectChanges();
    }
  }

  async refreshUsers() {
    this.isLoading = true;
    try {
      this.allUsers = await this.userService.getUsers();
      this.allUsers = this.allUsers.filter(
        (user) => !user.permissions.some((p) => p.permission_type === 'ACCOUNT_ADMIN')
      );

      this.updateUserReadOnlyGroups();
      this.fillRemainingUsers();
      const updatedUsers = [];
      const updatedGroupUsers = [];
      this.allUsers.forEach((user) => {
        if (this.group.members.some((member) => member === user.user_id)) {
          updatedUsers.push(user.user_id);
          updatedGroupUsers.push(user);
        }
      });
      this.groupUsers = updatedGroupUsers;
      this.group.members = updatedUsers;
      this.canDeleteGroup =
        !this.authService.isReadOnly &&
        this.groupList.length > 1 &&
        !this.group.members.length &&
        !this.groupProjectCount &&
        !this.groupConfigCount;
    } finally {
      this.isLoading = false;
    }
  }

  private updateUserReadOnlyGroups() {
    this.allUsers.forEach((user) => {
      this.userMemberships.set(
        user.user_id,
        new Map<string, string[]>(user.user_groups.map((g) => [g, []]))
      );

      for (const perm of user.permissions) {
        if (perm.group_id) {
          const group = this.userMemberships.get(user.user_id).get(perm.group_id);
          if (group) {
            group.push(perm.permission_type);
          }
        }
      }
    });
  }

  private fillRemainingUsers() {
    this.remainingUsers = this.allUsers.filter(
      (u) => !this.group.members.some((m) => m === u.user_id)
    );
  }

  copyGroupId() {
    this.systemService.copyToClipboard(this.group?.group_id);
    this.alertService.showToast(this.translocoService.translate('group-users.copied-to-clipboard'));
  }

  async onPermissionChanged() {
    await this.refreshUsers();
  }

  onNameUpdate() {
    const original = this.group.group_name;
    this.group.group_name = this.groupName.value;

    this.updateGroup(() => (this.group.group_name = original));
  }

  updateGroup(resetFn) {
    this.isLoading = true;
    this.groupService
      .updateGroup(this.group)
      .then(() => {
        this.isLoading = false;

        this.groupChange.emit(this.group);
      })
      .catch((err) => {
        this.isLoading = false;

        resetFn();

        this.alertService.showErrorToast(
          this.translocoService.translate('group-users.error.cannot-update'),
          err
        );
      });
  }

  get deleteButtonTooltip() {
    if (!this.canDeleteGroup) {
      return this.translocoService.translate('group-users.deletion.cannot-delete');
    }
  }

  deleteGroup() {
    const groupName = this.group.group_name;

    this.dialogService
      .showWarningPopup({
        title: this.translocoService.translate('group-users.deletion.popup-title'),
        content: this.translocoService.translate('group-users.deletion.popup-content', {
          groupName,
        }),
        confirmTitle: this.translocoService.translate('group-users.deletion.popup-confirm-title'),
        cancelTitle: this.translocoService.translate('group-users.deletion.cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.isLoading = true;

          this.groupService
            .deleteGroup(this.group.group_id)
            .then(() => {
              this.isLoading = false;

              this.groupDelete.emit(this.group);

              this.alertService.showToast(
                this.translocoService.translate('group-users.deletion.delete-success', {
                  groupName,
                })
              );
            })
            .catch((err) => {
              this.isLoading = false;

              this.alertService.showErrorToast(
                this.translocoService.translate('group-users.error.delete-failed', {
                  groupName,
                }),
                err
              );
            });
        }
      });
  }

  onAddUser() {
    this.showGroupUsersModal = true;
    this.groupUsersData = {
      group: this.group,
      allUsers: this.remainingUsers,
    };
  }

  async onGroupUsersModalClosed(res: SemantriaUser[]) {
    this.showGroupUsersModal = false;
    if (res) {
      this.group.members = this.group.members.concat(res.map((user) => user.user_id));
      await this.refreshUsers();
    }
  }

  async onUserRemoved($event: SemantriaUser) {
    this.group.members = this.group.members.filter((u) => u !== $event.user_id);
    await this.refreshUsers();
  }

  async onReloadGroup() {
    this.isLoading = true;
    this.group = await this.groupService.getGroup(this.group.group_id);
    this.isLoading = false;
  }
}
